import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { analyticsApi, exportApi, userApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { LiveMap } from '../components/LiveMap';
import { OnboardingWizard } from '../components/OnboardingWizard';
import { BeatTracker } from '../components/BeatTracker';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Activity, Users, Building2, Calendar, Phone, MapPin,
  CheckCircle, Clock, AlertTriangle, TrendingUp, RefreshCw,
  ArrowRight, FileSpreadsheet, FileText, BarChart3, Radar as RadarIcon,
  Navigation, Route, Gauge, Sparkles
} from 'lucide-react';

const PIE_COLORS_TYPE = ['#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b'];
const PIE_COLORS_PRI  = ['#ef4444', '#f59e0b', '#22c55e'];
const TASK_STATUS_COLORS = ['#f59e0b', '#3b82f6', '#22c55e'];
const RADAR_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899'];
const HEATMAP_COLORS = ['#1e293b', '#1e3a5f', '#1e4d8c', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-medium text-gray-800 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs">{p.name}: <span className="font-semibold">{p.value}</span></p>
      ))}
    </div>
  );
};

/* ─── Completion Rate Gauge ────────────────────── */
function CompletionGauge({ rate, period }) {
  const clampedRate = Math.min(Math.max(rate, 0), 100);
  const angle = (clampedRate / 100) * 180;
  const color = clampedRate >= 75 ? '#22c55e' : clampedRate >= 50 ? '#f59e0b' : '#ef4444';
  const label = clampedRate >= 75 ? 'Excellent' : clampedRate >= 50 ? 'Good' : 'Needs Improvement';

  return (
    <div className="flex flex-col items-center" data-testid="completion-gauge">
      <div className="relative w-48 h-28 overflow-hidden">
        {/* Background arc */}
        <svg viewBox="0 0 200 110" className="w-full h-full">
          <defs>
            <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
          {/* Track */}
          <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#e5e7eb" strokeWidth="14" strokeLinecap="round" />
          {/* Filled arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="url(#gaugeGrad)"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={`${(angle / 180) * 251.3} 251.3`}
            className="transition-all duration-1000 ease-out"
          />
          {/* Needle */}
          <line
            x1="100" y1="100"
            x2={100 + 65 * Math.cos(Math.PI - (angle * Math.PI / 180))}
            y2={100 - 65 * Math.sin(Math.PI - (angle * Math.PI / 180))}
            stroke={color} strokeWidth="3" strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
          <circle cx="100" cy="100" r="6" fill={color} className="transition-all duration-700" />
        </svg>
      </div>
      <div className="text-center -mt-2">
        <p className="text-4xl font-bold" style={{ color, fontFamily: 'Outfit' }}>{clampedRate}%</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
        <p className="text-[10px] text-gray-400 capitalize">{period} completion rate</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [completionPeriod, setCompletionPeriod] = useState('week');
  const [completionUser, setCompletionUser] = useState('all');
  const [salespersons, setSalespersons] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, perfRes, chartsRes] = await Promise.all([
        analyticsApi.getRealtime(),
        analyticsApi.getPerformance(completionPeriod),
        analyticsApi.getCharts(),
      ]);
      setAnalytics(analyticsRes.data);
      setPerformance(perfRes.data);
      setCharts(chartsRes.data);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [completionPeriod]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (isAdminCheck()) {
      userApi.getSalespersons().then(r => setSalespersons(r.data || [])).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function isAdminCheck() {
    return user?.role === 'admin' || user?.role === 'manager' || user?.role === 'owner';
  }
  const isAdmin = isAdminCheck();

  const handleRefresh = async () => {
    setRefreshing(true);
    setLoading(true);
    setAnalytics(null);
    setPerformance(null);
    setCharts(null);
    await new Promise(r => setTimeout(r, 400));
    await fetchData();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleExport = async (type, format) => {
    try {
      let res;
      if (type === 'tasks') res = await exportApi.tasks(format);
      else if (type === 'activities') res = await exportApi.activities(format);
      else res = await exportApi.performance(format, completionPeriod);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} report downloaded`);
    } catch { toast.error('Export failed'); }
  };

  // Compute completion rate for selected user
  const getCompletionRate = () => {
    if (completionUser === 'all') return performance?.completion_rate || 0;
    const sp = charts?.performer_rankings?.find(p => p.id === completionUser);
    return sp?.rate || 0;
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color, onClick, animate }) => (
    <Card className={`border-gray-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ${onClick ? 'cursor-pointer' : ''} ${animate ? 'animate-in fade-in slide-in-from-bottom-3 duration-500' : ''}`}
      onClick={onClick} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 font-medium">{label}</p>
            <p className="text-3xl font-semibold text-gray-900 mt-1" style={{ fontFamily: 'Outfit' }}>{value}</p>
            {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
          </div>
          <div className={`p-3 rounded-xl ${color} transition-transform hover:scale-110`}><Icon className="w-5 h-5" /></div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto">
              <div className="w-16 h-16 border-4 border-blue-200 rounded-full" />
              <div className="absolute inset-0 w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="mt-4 text-gray-500 animate-pulse">Loading dashboard...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="dashboard-page">

        <OnboardingWizard />
        <BeatTracker />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              {isAdmin ? 'Dashboard' : 'My Dashboard'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">Welcome back, {user?.name}!</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 animate-pulse">
              <Sparkles className="w-3 h-3 mr-1" /> Live
            </Badge>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn"
              className="transition-all hover:bg-blue-50 hover:border-blue-200">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Activity} label="Total Activities" value={analytics?.summary?.total_activities || 0}
            subValue={`${analytics?.summary?.today_activities || 0} today`} color="bg-blue-100 text-blue-600" onClick={() => navigate('/activities')} animate />
          <StatCard icon={AlertTriangle} label="High Priority" value={analytics?.summary?.high_priority_pending || 0}
            subValue="Needs attention" color="bg-red-100 text-red-600" onClick={() => navigate('/activities?priority=high')} animate />
          <StatCard icon={CheckCircle} label="Completed Today" value={analytics?.summary?.completed_today || 0}
            subValue={`${performance?.completion_rate || 0}% this week`} color="bg-green-100 text-green-600" onClick={() => navigate('/activities?status=completed')} animate />
          <StatCard icon={Clock} label="Pending" value={analytics?.summary?.pending || 0}
            subValue="To be completed" color="bg-yellow-100 text-yellow-600" onClick={() => navigate('/activities?status=pending')} animate />
        </div>

        {/* ===== ROW 1: Weekly Trend (Line + Bar) + Completion Rate Gauge ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Weekly Activity Trend - Line Graph + Histogram */}
          <Card className="lg:col-span-2 border-gray-200 hover:shadow-md transition-shadow" data-testid="weekly-trend-chart">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  <BarChart3 className="w-5 h-5 inline mr-2 text-blue-500" />Weekly Activity Trend
                </CardTitle>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleExport('activities', 'excel')} data-testid="export-activities-excel">
                    <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleExport('performance', 'pdf')} data-testid="export-performance-pdf">
                    <FileText className="w-3 h-3 mr-1" /> PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {charts?.weekly_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={charts.weekly_trend} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                    onClick={(e) => { if (e?.activePayload) navigate('/activities'); }}>
                    <defs>
                      <linearGradient id="barGradCreated" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.4} />
                      </linearGradient>
                      <linearGradient id="barGradCompleted" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0.4} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="day" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9', radius: 4 }} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="created" name="Created" fill="url(#barGradCreated)" radius={[6, 6, 0, 0]} barSize={24} style={{ cursor: 'pointer' }} animationDuration={800} />
                    <Bar dataKey="completed" name="Completed" fill="url(#barGradCompleted)" radius={[6, 6, 0, 0]} barSize={24} style={{ cursor: 'pointer' }} animationDuration={1000} />
                    <Line type="monotone" dataKey="created" stroke="#1d4ed8" strokeWidth={2} dot={false} strokeDasharray="5 5" legendType="none" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[280px] text-gray-400">
                  <div className="text-center"><BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No activity data yet</p></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Task Completion Rate Gauge with Filters */}
          <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="completion-rate-gauge">
            <CardHeader className="pb-1">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                <Gauge className="w-5 h-5 inline mr-2 text-teal-500" />Completion Rate
              </CardTitle>
              <div className="flex gap-2 mt-2">
                <Select value={completionPeriod} onValueChange={setCompletionPeriod}>
                  <SelectTrigger className="h-7 text-xs w-24" data-testid="completion-period-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <Select value={completionUser} onValueChange={setCompletionUser}>
                    <SelectTrigger className="h-7 text-xs flex-1" data-testid="completion-user-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      {salespersons.map(sp => (
                        <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <CompletionGauge rate={getCompletionRate()} period={completionPeriod} />
            </CardContent>
          </Card>
        </div>

        {/* ===== ROW 2: Task Status Doughnut + Activity/Priority Doughnuts ===== */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Task Status Doughnut */}
          <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="task-status-doughnut">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>
                <CheckCircle className="w-4 h-4 inline mr-1.5 text-emerald-500" />Task Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {charts?.task_status_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={charts.task_status_distribution} cx="50%" cy="50%"
                      innerRadius={48} outerRadius={72} paddingAngle={4} dataKey="value"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(user?.role === 'salesperson' ? '/my-tasks' : '/tasks')}
                      animationDuration={800}>
                      {charts.task_status_distribution.map((_, i) => (
                        <Cell key={i} fill={TASK_STATUS_COLORS[i % TASK_STATUS_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val, name) => [val, name]} />
                    <Legend wrapperStyle={{ fontSize: '11px', cursor: 'pointer' }} iconSize={8}
                      onClick={() => navigate(user?.role === 'salesperson' ? '/my-tasks' : '/tasks')} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[240px] text-gray-400 text-sm">No task data</div>
              )}
            </CardContent>
          </Card>

          {/* Activity Type */}
          <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="activity-type-pie">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>
                <Activity className="w-4 h-4 inline mr-1.5 text-blue-500" />Activity Types
              </CardTitle>
            </CardHeader>
            <CardContent>
              {charts?.activity_type_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={charts.activity_type_distribution} cx="50%" cy="50%"
                      innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate('/activities')}
                      animationDuration={900}>
                      {charts.activity_type_distribution.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS_TYPE[i % PIE_COLORS_TYPE.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val, name) => [val, name]} />
                    <Legend wrapperStyle={{ fontSize: '11px', cursor: 'pointer' }} iconSize={8}
                      onClick={() => navigate('/activities')} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[240px] text-gray-400 text-sm">No data</div>
              )}
            </CardContent>
          </Card>

          {/* Priority Split */}
          <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="priority-doughnut">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>
                <AlertTriangle className="w-4 h-4 inline mr-1.5 text-red-400" />Priority Split
              </CardTitle>
            </CardHeader>
            <CardContent>
              {charts?.priority_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={charts.priority_distribution} cx="50%" cy="50%"
                      innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate('/activities')}
                      animationDuration={1000}>
                      {charts.priority_distribution.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS_PRI[i % PIE_COLORS_PRI.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val, name) => [val, name]} />
                    <Legend wrapperStyle={{ fontSize: '11px', cursor: 'pointer' }} iconSize={8}
                      onClick={() => navigate('/activities')} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[240px] text-gray-400 text-sm">No data</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ===== ROW 3: Live Map + Radar Chart (Admin/Manager/Owner) ===== */}
        {isAdmin ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="live-map-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                    <MapPin className="w-5 h-5 inline mr-2 text-emerald-500" />Live Team Locations
                  </CardTitle>
                  <Button variant="outline" size="sm" className="text-xs hover:bg-emerald-50" onClick={() => navigate('/live-tracking')} data-testid="goto-live-tracking">
                    <Navigation className="w-3 h-3 mr-1" /> Live Tracking
                  </Button>
                </div>
              </CardHeader>
              <CardContent><LiveMap /></CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="radar-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  <RadarIcon className="w-5 h-5 inline mr-2 text-blue-500" />Team Performance Radar
                </CardTitle>
              </CardHeader>
              <CardContent>
                {charts?.radar_data?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <RadarChart data={[
                      { axis: 'Activities', ...Object.fromEntries(charts.radar_data.map(r => [r.name, r.activities])) },
                      { axis: 'Completion', ...Object.fromEntries(charts.radar_data.map(r => [r.name, r.completion])) },
                      { axis: 'Check-ins', ...Object.fromEntries(charts.radar_data.map(r => [r.name, r.checkins])) },
                      { axis: 'Tasks', ...Object.fromEntries(charts.radar_data.map(r => [r.name, r.tasks])) },
                      { axis: 'Field Beats', ...Object.fromEntries(charts.radar_data.map(r => [r.name, r.field_beats])) },
                    ]}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="axis" tick={{ fill: '#64748b', fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                      {charts.radar_data.map((sp, i) => (
                        <Radar key={sp.name} name={sp.name} dataKey={sp.name}
                          stroke={RADAR_COLORS[i % RADAR_COLORS.length]} fill={RADAR_COLORS[i % RADAR_COLORS.length]}
                          fillOpacity={0.12} strokeWidth={2} style={{ cursor: 'pointer' }}
                          onClick={() => navigate('/users')} animationDuration={800 + i * 200} />
                      ))}
                      <Legend wrapperStyle={{ fontSize: '11px', cursor: 'pointer' }} onClick={() => navigate('/users')} />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[320px] text-gray-400 text-sm">No salesperson data</div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>My Location</CardTitle>
            </CardHeader>
            <CardContent><LiveMap /></CardContent>
          </Card>
        )}

        {/* ===== ROW 4 (Admin): Performance Bar + Customer Heatmap ===== */}
        {isAdmin && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="performer-rankings-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  <Users className="w-5 h-5 inline mr-2 text-indigo-500" />Salesperson Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {charts?.performer_rankings?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.performer_rankings} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
                      onClick={() => navigate('/users')}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={90} tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '12px', cursor: 'pointer' }} />
                      <Bar dataKey="completed" name="Completed" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={12} style={{ cursor: 'pointer' }} animationDuration={800} />
                      <Bar dataKey="checkins" name="Check-ins" fill="#22c55e" radius={[0, 4, 4, 0]} barSize={12} style={{ cursor: 'pointer' }} animationDuration={1000} />
                      <Bar dataKey="beats" name="Beats" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={12} style={{ cursor: 'pointer' }} animationDuration={1200} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm">No salesperson data</div>
                )}
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="customer-heatmap-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  <Building2 className="w-5 h-5 inline mr-2 text-purple-500" />Customer Engagement
                </CardTitle>
              </CardHeader>
              <CardContent>
                {charts?.customer_heatmap?.length > 0 ? (
                  <div className="space-y-1.5" data-testid="heatmap-grid">
                    <div className="grid grid-cols-[1fr_60px_60px_60px] gap-1 text-xs text-gray-500 font-medium px-1">
                      <span>Customer</span>
                      <span className="text-center">Visits</span>
                      <span className="text-center">Meets</span>
                      <span className="text-center">Total</span>
                    </div>
                    {charts.customer_heatmap.map((c, idx) => {
                      const maxTotal = Math.max(...charts.customer_heatmap.map(x => x.total), 1);
                      const intensity = Math.min(Math.floor((c.total / maxTotal) * 6), 6);
                      return (
                        <div key={idx}
                          className="grid grid-cols-[1fr_60px_60px_60px] gap-1 items-center px-1 py-1.5 rounded-md cursor-pointer hover:bg-gray-100 transition-all duration-200 hover:translate-x-1"
                          onClick={() => navigate('/customers')} data-testid={`heatmap-row-${idx}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-2 h-8 rounded-sm transition-colors" style={{ backgroundColor: HEATMAP_COLORS[intensity] }} />
                            <div className="truncate">
                              <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                              <p className="text-[10px] text-gray-400">{c.city}</p>
                            </div>
                          </div>
                          <div className="text-center"><span className={`inline-block w-9 h-7 leading-7 rounded text-xs font-semibold ${c.visits > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-300'}`}>{c.visits}</span></div>
                          <div className="text-center"><span className={`inline-block w-9 h-7 leading-7 rounded text-xs font-semibold ${c.meetings > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-50 text-gray-300'}`}>{c.meetings}</span></div>
                          <div className="text-center"><span className={`inline-block w-9 h-7 leading-7 rounded text-xs font-bold ${c.total > 0 ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-300'}`}>{c.total}</span></div>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-1 mt-3 px-1">
                      <span className="text-[10px] text-gray-400">Low</span>
                      {HEATMAP_COLORS.map((c, i) => (<div key={i} className="w-5 h-3 rounded-sm" style={{ backgroundColor: c }} />))}
                      <span className="text-[10px] text-gray-400">High</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm">No customer visit data</div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== ROW 5 (Admin): GPS Beat Stats ===== */}
        {isAdmin && charts?.beat_stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={Navigation} label="Active Beats" value={charts.beat_stats.active_now || 0}
              subValue="Field agents on beat now" color="bg-emerald-100 text-emerald-600" onClick={() => navigate('/live-tracking')} animate />
            <StatCard icon={Route} label="Completed Beats" value={charts.beat_stats.completed_today || 0}
              subValue="Total beats completed" color="bg-blue-100 text-blue-600" onClick={() => navigate('/live-tracking')} animate />
            <StatCard icon={MapPin} label="Total Distance" value={`${charts.beat_stats.total_distance_km || 0} km`}
              subValue="Covered by field team" color="bg-purple-100 text-purple-600" onClick={() => navigate('/live-tracking')} animate />
          </div>
        )}

        {/* Activity Breakdown + Upcoming */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="activity-breakdown">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Activity Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: MapPin, color: 'bg-blue-50 text-blue-600', value: analytics?.activity_types?.visits || 0, label: 'Site Visits' },
                  { icon: Users, color: 'bg-purple-50 text-purple-600', value: analytics?.activity_types?.meetings || 0, label: 'Meetings' },
                  { icon: Phone, color: 'bg-green-50 text-green-600', value: analytics?.activity_types?.calls || 0, label: 'Calls' },
                  { icon: CheckCircle, color: 'bg-orange-50 text-orange-600', value: analytics?.activity_types?.tasks || 0, label: 'Tasks' },
                ].map(({ icon: Icon, color, value, label }) => (
                  <div key={label} className={`p-4 ${color.split(' ')[0]} rounded-xl text-center cursor-pointer hover:scale-105 transition-transform duration-200`}
                    onClick={() => navigate('/activities')} data-testid={`breakdown-${label.toLowerCase().replace(/\s/g, '-')}`}>
                    <Icon className={`w-6 h-6 ${color.split(' ')[1]} mx-auto mb-2`} />
                    <p className="text-2xl font-semibold text-gray-900">{value}</p>
                    <p className="text-sm text-gray-500">{label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="upcoming-activities">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Upcoming Activities</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/activities')}>
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics?.upcoming_activities?.length > 0 ? (
                  analytics.upcoming_activities.slice(0, 5).map((a) => (
                    <div key={a.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 hover:translate-x-1 transition-all duration-200 cursor-pointer" onClick={() => navigate('/activities')} data-testid={`upcoming-${a.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{a.title}</p>
                          <p className="text-sm text-gray-500 truncate">{a.customer_name}</p>
                        </div>
                        <div className="text-right ml-4">
                          <Badge className={`${a.priority === 'high' ? 'bg-red-100 text-red-700' : a.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                            {a.priority}
                          </Badge>
                          <p className="text-xs text-gray-400 mt-1">{a.scheduled_date}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    <p>No upcoming activities</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Performers */}
        {isAdmin && performance?.top_performers?.length > 0 && (
          <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid="top-performers">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Top Performers This Week</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {performance.top_performers.map((p, idx) => (
                  <div key={p.name || idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 hover:scale-105 transition-all duration-200"
                    onClick={() => navigate('/users')} data-testid={`top-performer-${idx}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-white ${
                      idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-400' : 'bg-blue-500'
                    }`}>{idx + 1}</div>
                    <div>
                      <p className="font-medium text-gray-900">{p.name}</p>
                      <p className="text-sm text-gray-500">{p.completed} completed</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </MainLayout>
  );
}
